import { HowToAnswer } from "../components/interactions/HowToAnswer";
/**
 * WCC360 v2 — "Rank your top three causes" (X-CAUSE-001).
 *
 * Everyone says they care about all twenty-four purpose areas, which makes an
 * unranked list almost useless: it discriminates nothing. A forced ranking is
 * a real trade-off, and a trade-off is evidence.
 *
 * Interaction: tap to add in order (1, 2, 3), tap again to remove, explicit
 * "no single one stands out" escape that is honoured rather than scored, and
 * full keyboard operation — every target is a real button with a visible
 * focus ring and an accessible position label.
 *
 * Bounded scoring plus cause-led next steps on the result. See `causes.ts`.
 */
import { ArrowRight, X } from "lucide-react";
import { useState } from "react";
import { MAX_RANKED_CAUSES, ORDINAL, causeById } from "../causes";
import { CAUSE_EXAMPLE, CAUSE_GROUPS } from "../causeCatalogue";
import { useAssessmentV2 } from "../store";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { confirmSuggestedCause, suggestCause } from "../causeSuggestion";

const COPY: Record<string, { title: string; subtitle: string }> = {
  "12to14": {
    title: "What would you like to help make better?",
    subtitle:
      "Pick up to three, best one first. You don't need to know how yet. You care about all of them — we just want to know which one you'd start on.",
  },
  "15to18": {
    title: "What would you like to help make better?",
    subtitle:
      "Pick up to three in order. You don't need to know how yet. Saying one matters more than another isn't a betrayal of the others — it's how you find work you'll stay in.",
  },
  "18plus": {
    title: "What would you like to help make better?",
    subtitle:
      "Pick up to three in order of priority. You don't need to know how yet. The order matters: it changes both what we match you to and what we tell you to do next.",
  },
};

interface Props { onNext: () => void }

export function CauseRankScreen({ onNext }: Props) {
  const { state, setPart1, band } = useAssessmentV2();
  const copy = COPY[band] ?? COPY["18plus"];

  const [ranked, setRanked] = useState<string[]>(state.part1.cause_rank_ids ?? []);
  const [none, setNone] = useState(
    Array.isArray(state.part1.cause_rank_ids) && state.part1.cause_rank_ids.length === 0,
  );

  const [other, setOther] = useState(state.part1.cause_other ?? "");
  const [openEx, setOpenEx] = useState<string | null>(null);
  const full = ranked.length >= MAX_RANKED_CAUSES;
  const canContinue = none || ranked.length > 0;
  const suggestion = suggestCause(other);
  const suggestionSelected = suggestion ? ranked.includes(suggestion.id) : false;

  const toggle = (id: string) => {
    setNone(false);
    setRanked((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length >= MAX_RANKED_CAUSES
          ? prev
          : [...prev, id],
    );
  };

  const commit = () => {
    setPart1({ cause_rank_ids: none ? [] : ranked, cause_other: other.trim() || undefined });
    onNext();
  };

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col">
      <h2 className="font-display text-2xl md:text-3xl font-medium leading-snug">
        {copy.title}
      </h2>
      <p className="mt-3 text-muted-foreground leading-relaxed">{copy.subtitle}</p>
      <div className="mt-4"><HowToAnswer>Tap up to {MAX_RANKED_CAUSES} in order of importance — first tap is your top priority. Tap again to remove.</HowToAnswer></div>

      <p className="mt-4 text-xs uppercase tracking-[0.16em] text-primary" aria-live="polite">
        {none
          ? "Still exploring — that's a real answer"
          : ranked.length === 0
            ? "Tap your first priority"
            : `${ranked.length} of ${MAX_RANKED_CAUSES} ranked`}
      </p>

      {ranked.length > 0 && (
        <ol className="mt-4 space-y-2">
          {ranked.map((id, i) => (
            <li key={id}>
              <button
                type="button"
                onClick={() => toggle(id)}
                className="w-full min-h-[52px] flex items-center gap-3 rounded-2xl border border-primary/40
                  bg-primary/10 px-4 py-3 text-left
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <span
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary
                    text-xs font-medium text-primary-foreground tabular-nums"
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1 text-sm font-medium">
                  {causeById(id)?.label}
                  <span className="sr-only"> — your {ORDINAL[i] ?? "next"} priority. Activate to remove.</span>
                </span>
                <X className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ol>
      )}

      {CAUSE_GROUPS.map((g) => (
        <section key={g.id} className="mt-6" aria-labelledby={`cause-group-${g.id}`}>
          <h3 id={`cause-group-${g.id}`} className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
            {g.label}
          </h3>
          <ul className="mt-2 grid gap-2 sm:grid-cols-2">
            {g.ids.map((cid) => causeById(cid)).filter((c): c is NonNullable<typeof c> => Boolean(c)).map((c) => {
              const idx = ranked.indexOf(c.id);
              const on = idx >= 0;
              const showEx = openEx === c.id;
              return (
                <li key={c.id} className="flex flex-col">
                  <button
                    type="button"
                    aria-pressed={on}
                    disabled={!on && full}
                    onClick={() => toggle(c.id)}
                    className={cn(
                      "w-full min-h-[56px] rounded-2xl border px-4 py-3 text-left flex items-start gap-3",
                      "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                      on ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40",
                      !on && full && "opacity-40 cursor-not-allowed",
                    )}
                  >
                    <span
                      className={cn(
                        "mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border text-xs tabular-nums",
                        on ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground",
                      )}
                      aria-hidden="true"
                    >
                      {on ? idx + 1 : ""}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{c.label}</span>
                      <span className="block text-xs text-muted-foreground">{c.description}</span>
                    </span>
                  </button>
                  {CAUSE_EXAMPLE[c.id] && (
                    <button
                      type="button"
                      aria-expanded={showEx}
                      onClick={() => setOpenEx(showEx ? null : c.id)}
                      className="self-start mt-1 min-h-[44px] px-2 text-xs text-primary underline-offset-2 hover:underline
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                    >
                      {showEx ? "Hide example" : "See an example"}
                      <span className="sr-only"> for {c.label}</span>
                    </button>
                  )}
                  {showEx && <p className="px-2 pb-1 text-xs text-muted-foreground">{CAUSE_EXAMPLE[c.id]}</p>}
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      <div className="mt-6">
        <label htmlFor="cause-other" className="block text-sm font-medium">Something else?</label>
        <p id="cause-other-help" className="text-xs text-muted-foreground">Optional. Tell us what you'd like to make better. Confirm a suggested area to include it in your career matches. Your original words stay with your answers.</p>
        <input
          id="cause-other"
          type="text"
          maxLength={120}
          aria-describedby="cause-other-help"
          value={other}
          onChange={(e) => setOther(e.target.value)}
          className="mt-2 w-full min-h-[48px] rounded-2xl border border-border bg-card px-4 text-sm
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          placeholder="e.g. road safety in my town"
        />
        <div className="mt-3 text-sm" aria-live="polite">
          {suggestion ? (
            <>
              <p className="font-medium">Suggested area: {suggestion.label}</p>
              <p className="mt-1 text-xs text-muted-foreground">Inferred from “{suggestion.matchedTerms.join('”, “')}”. You decide whether it fits. Confirmed choices use your existing cause ranking alongside your interests and how you'd like to help, without ruling careers out.</p>
              <Button
                type="button"
                variant="outline"
                className="mt-2 min-h-12 h-auto whitespace-normal text-left"
                disabled={suggestionSelected || full}
                onClick={() => { setNone(false); setRanked((prev) => confirmSuggestedCause(prev, suggestion.id)); }}
              >
                {suggestionSelected ? "Included in your priorities" : "Use this area"}
              </Button>
              {full && !suggestionSelected && <p className="mt-2 text-xs text-muted-foreground">You've used all 3 cause picks — remove one above to use this area.</p>}
            </>
          ) : other.trim().length >= 3 ? (
            <p className="text-xs text-muted-foreground">No clear nearby area yet. Try more specific words or choose an area above. Your own words will still be saved; we won't guess a match.</p>
          ) : null}
        </div>
      </div>

      {/* Honoured, not scored as a midpoint. */}
      <button
        type="button"
        aria-pressed={none}
        onClick={() => { setNone((v) => !v); setRanked([]); }}
        className={cn(
          "mt-4 w-full min-h-[56px] rounded-2xl border px-5 py-4 text-left",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
          none ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40",
        )}
      >
        <span className="block text-sm font-medium">I'm still exploring</span>
        <span className="block text-xs text-muted-foreground">
          That's a real answer. We'll leave causes out of your matching rather than guess.
        </span>
      </button>

      <div className="mt-8">
        <button
          onClick={commit}
          disabled={!canContinue}
          className="min-h-[52px] w-full sm:w-auto px-7 rounded-2xl bg-primary text-primary-foreground
            font-medium inline-flex items-center justify-center gap-2 disabled:opacity-40
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Continue <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
        {!canContinue && (
          <p className="mt-3 text-xs text-muted-foreground">
            Rank at least one, or choose "I'm still exploring".
          </p>
        )}
      </div>
    </div>
  );
}
